import { FC, useState } from 'react';

import mailImg from 'assets/brand/mail@2x.png';
import getErrorMessageIfPossible from 'common/hooks/useErrorHandledMutation/getErrorMessageIfPossible';
import Button from 'components/_core/Button';
import SunshineModal from 'components/_core/SunshineModal';
import useToast from 'components/ToastProvider/useToast';
import { logEvent } from 'features/Analytics/analytics';
import { ContactOptions } from 'features/Work/components/ContactPicker';
import SelectContactsToSendTo from 'features/Work/components/SelectContactsToSendTo';
import useSendDummyInvoiceToUser from 'features/Work/pages/Invoices/hooks/mutations/useSendDummyInvoiceToUser';
import useSendInvoice from 'features/Work/pages/Invoices/hooks/mutations/useSendInvoice';
import { SendingOptionsEnum } from 'features/Work/types';

import { ResendWithShineModalProps } from '../..';
import locales from './locales';

interface ResendWithShineModalBodyProps
  extends Pick<ResendWithShineModalProps, 'clientId' | 'invoiceId'> {
  contactOptions: ContactOptions;
  hideModal: () => void;
  onShowContact: () => void;
  onSelectContacts: (contacts: string[]) => void;
}

const ResendWithShineModalBody: FC<ResendWithShineModalBodyProps> = ({
  clientId,
  contactOptions,
  hideModal,
  invoiceId,
  onSelectContacts,
  onShowContact,
}) => {
  const { showToast } = useToast();

  const { loading: sendLoading, onSendInvoice } = useSendInvoice();
  const { loading: sendDummyLoading, sendDummyInvoiceToUser } =
    useSendDummyInvoiceToUser();

  const [isUserCcedOnEmails, setIsUserCcedOnEmails] = useState(false);

  const onSend = async () => {
    try {
      logEvent({
        name: 'Invoice Resent',
        properties: {
          from: 'ResendInvoiceModal',
          invoiceId,
          method: SendingOptionsEnum.withShine,
        },
      });

      await onSendInvoice({
        input: {
          clientContactIds: contactOptions.clientContactIds,
          invoiceId,
          isUserCcedOnEmails,
        },
      });

      showToast({
        message: locales.successMessage,
        type: 'success',
      });

      hideModal();
    } catch (error) {
      showToast({
        message: getErrorMessageIfPossible({ error }) || locales.errorMessage,
        type: 'error',
      });
    }
  };

  const onSendDummyItemClick = async () => {
    logEvent({
      name: 'Send Dummy Invoice To User',
      properties: { invoiceId },
    });

    try {
      await sendDummyInvoiceToUser({ input: { invoiceId } });

      showToast({
        message: locales.sendDummyInvoiceSuccess,
        type: 'success',
      });
    } catch (error) {
      showToast({
        message: locales.sendDummyInvoiceError,
        type: 'error',
      });
    }
  };

  return (
    <>
      <SunshineModal.Header />

      <SunshineModal.Body>
        <SunshineModal.Body.Image src={mailImg} />
        <SunshineModal.Title>{locales.title}</SunshineModal.Title>
        <SelectContactsToSendTo
          checkboxId="cc-user-send-item-with-shine"
          checked={isUserCcedOnEmails}
          clientId={clientId}
          onChangeCheckbox={setIsUserCcedOnEmails}
          onChangeSelect={onSelectContacts}
          onClickAddOption={onShowContact}
          selectValue={contactOptions.clientContactIds}
        />
      </SunshineModal.Body>

      <SunshineModal.Footer>
        <Button
          isDisabled={sendLoading}
          isLoading={sendDummyLoading}
          onClick={onSendDummyItemClick}
          variant="inline-secondary"
        >
          {locales.sendToMyself}
        </Button>

        <Button
          isDisabled={
            sendDummyLoading || !contactOptions.clientContactIds.length
          }
          isLoading={sendLoading}
          onClick={onSend}
        >
          {locales.send}
        </Button>
      </SunshineModal.Footer>
    </>
  );
};

export default ResendWithShineModalBody;
